import { createSlice } from '@reduxjs/toolkit'
import { createPrice, getCountryPrices, getDefaultPrices, getUserPrices, updateCustomPrice } from 'Api/admin/admin.pricing.service.ts'
import { PriceType } from 'Types/admin/admin'
import { DefaultPriceProps } from 'Pages/AdminPricing/AdminPricing'

interface PriceProps {
  certificate: { [key: string]: PriceType[] }
  purchase: { [key: string]: PriceType[] }
  dhl: { [key: string]: PriceType[] }
}

interface CustomerPriceProps {
  certificate: PriceType[]
  purchase: PriceType[]
}

interface PricesState {
  defaultPrice: DefaultPriceProps
  countryPrices: PriceProps
  userPrices: CustomerPriceProps
}

const initialState: PricesState = {
  defaultPrice: null,
  countryPrices: null,
  userPrices: null,
}

export const pricesSlice = createSlice({
  name: 'pricesSlice',
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(createPrice.fulfilled, (state, action) => {
        if (action.payload) {
          switch (action.payload.price.type) {
            case 'DEFAULT': {
              if (action.payload.price.for === 'CERTIFICATE') {
                state.defaultPrice.certificate.push(action.payload.price)
              } else if (action.payload.price.for === 'PURCHASE') {
                state.defaultPrice.purchase.push(action.payload.price)
              } else if (action.payload.price.for === 'DHL') {
                if (!state.defaultPrice.dhl) {
                  state.defaultPrice.dhl = []
                }
                state.defaultPrice.dhl.push(action.payload.price)
              }
              break
            }
            case 'COUNTRY': {
              const countryName = action.payload.price.country
              if (action.payload.price.for === 'CERTIFICATE') {
                if (state.countryPrices.certificate[countryName]) {
                  state.countryPrices.certificate[countryName].push(action.payload.price)
                } else {
                  state.countryPrices.certificate[countryName] = [action.payload.price]
                }
              } else if (action.payload.price.for === 'PURCHASE') {
                if (state.countryPrices.purchase[countryName]) {
                  state.countryPrices.purchase[countryName].push(action.payload.price)
                } else {
                  state.countryPrices.purchase[countryName] = [action.payload.price]
                }
              } else if (action.payload.price.for === 'DHL') {
                if (!state.countryPrices.dhl) {
                  state.countryPrices.dhl = {}
                }
                if (state.countryPrices.dhl[countryName]) {
                  state.countryPrices.dhl[countryName].push(action.payload.price)
                } else {
                  state.countryPrices.dhl[countryName] = [action.payload.price]
                }
              }
              break
            }
          }
        }
      })
      .addCase(getDefaultPrices.fulfilled, (state, action) => {
        if (action.payload) {
          state.defaultPrice = action.payload.prices
        }
      })
      .addCase(getCountryPrices.fulfilled, (state, action) => {
        state.countryPrices = action.payload.prices
      })
      .addCase(getUserPrices.fulfilled, (state, action) => {
        if (action.payload) {
          // FIX ESLINT
          // @ts-ignore
          state.userPrices = action.payload.prices
        }
      })
      .addCase(updateCustomPrice.fulfilled, (state, action) => {
        if (action.payload?.price) {
          const updatedPrice = action.payload.price as unknown as PriceType

          // Helper function to update price in array
          const updatePriceInArray = (priceArray: PriceType[]) => {
            const index = priceArray.findIndex(p => p.id === updatedPrice.id)
            if (index !== -1) {
              priceArray[index] = { ...priceArray[index], ...updatedPrice }
            }
          }

          // Update DEFAULT prices
          if (updatedPrice.type === 'DEFAULT') {
            if (updatedPrice.for === 'CERTIFICATE' && state.defaultPrice?.certificate) {
              updatePriceInArray(state.defaultPrice.certificate)
            } else if (updatedPrice.for === 'PURCHASE' && state.defaultPrice?.purchase) {
              updatePriceInArray(state.defaultPrice.purchase)
            } else if (updatedPrice.for === 'DHL' && state.defaultPrice?.dhl) {
              updatePriceInArray(state.defaultPrice.dhl)
            } else if (updatedPrice.for === 'EXA' && state.defaultPrice?.exa) {
              updatePriceInArray(state.defaultPrice.exa)
            }
          }

          // Update COUNTRY prices
          if (updatedPrice.type === 'COUNTRY' && updatedPrice.country) {
            const countryName = updatedPrice.country
            if (updatedPrice.for === 'CERTIFICATE' && state.countryPrices?.certificate?.[countryName]) {
              updatePriceInArray(state.countryPrices.certificate[countryName])
            } else if (updatedPrice.for === 'PURCHASE' && state.countryPrices?.purchase?.[countryName]) {
              updatePriceInArray(state.countryPrices.purchase[countryName])
            } else if (updatedPrice.for === 'DHL' && state.countryPrices?.dhl?.[countryName]) {
              updatePriceInArray(state.countryPrices.dhl[countryName])
            }
          }
        }
      })
  },
})

export default pricesSlice.reducer
